import { useId, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useSession } from "../hooks/useSession";
import { useProductStockSearch } from "../hooks/useProductStockSearch";
import { ProductStockSearchResult } from "./ProductStockSearchResult";

/** Búsqueda del portal: el listado consulta nombres y referencias en la API autorizada. */
export function RequestSearch({ initialQuery = "", presentation = "popup", onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery);
  const [open, setOpen] = useState(false);
  const anchor = useRef(null);
  const popupId = useId();
  const { user } = useSession();
  const staff = ["admin", "manager", "commercial", "orders"].includes(user?.role);
  const showStock = staff && open && query.trim().length >= 2;
  const stock = useProductStockSearch(query, showStock);
  const inline = presentation === "inline";
  const resultPhase = stock.loading ? "loading" : stock.error ? "error" : stock.data?.pending ? "pending" : "ready";
  // En PC, si el detalle no cabe en alto, la ventana se ensancha hacia la derecha (animada) y el
  // contenido pasa a dos columnas en lugar de mostrar scroll. Si además se muestran los pedidos de
  // clientes, se ensancha lo necesario para ver su tabla completa. En móvil sigue el scroll.
  const desktop = useMediaQuery(theme => theme.breakpoints.up("md"), { noSsr: true });
  const paper = useRef(null);
  const [wide, setWide] = useState({ width: null, layout: false, mode: "columns" });
  const wideWidth = desktop ? wide.width : null;
  const wideLayout = desktop && wide.layout ? wide.mode : false;
  const resetWide = () => setWide({ width: null, layout: false, mode: "columns" });
  useLayoutEffect(() => {
    const node = paper.current;
    if (inline || !desktop || !showStock || resultPhase === "loading" || !node || !anchor.current) return;
    const orders = Boolean(node.querySelector("[data-stock-customer-orders] table"));
    const tall = node.scrollHeight > node.clientHeight + 1;
    if (!orders && !tall) return;
    const room = window.innerWidth - anchor.current.getBoundingClientRect().left - 16;
    const width = Math.min(orders ? 1180 : 900, room);
    // La tabla de pedidos de clientes cabe junto a las entradas si hay sitio; si no, va debajo.
    const mode = orders && width >= 1140 ? "beside" : "columns";
    // Sin sitio para dos columnas (o para la tabla de pedidos) se queda el scroll.
    if (width < (orders ? 760 : 640) || (wide.width && wide.width >= width)) return;
    const reduced = Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
    setWide(current => ({ width, mode, layout: current.layout || reduced }));
  }, [inline, desktop, showStock, resultPhase, stock.data, wide.width]);
  const dismiss = (restoreFocus = false) => {
    setOpen(false);
    resetWide();
    onClose?.(restoreFocus);
  };
  const submit = (event) => {
    event.preventDefault();
    const value = query.trim();
    dismiss();
    navigate(value ? `/solicitudes?q=${encodeURIComponent(value)}` : "/solicitudes");
  };
  const result = showStock && <Paper ref={inline ? undefined : paper} key={inline ? resultPhase : undefined} className={inline ? "adl-mobile-search-result" : undefined} id={popupId} role="region" aria-labelledby={`${popupId}-title`} elevation={inline ? 0 : 8} sx={{ borderRadius: "12px", border: "1px solid", borderColor: "divider", overflow: "auto", maxHeight: inline ? undefined : "calc(100dvh - 100px)", mt: inline ? 1.25 : undefined,
    "@media (prefers-reduced-motion: reduce)": { "& .MuiSkeleton-root::after": { animation: "none" } } }}>
    <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1, bgcolor: "#352b6b", color: "#fff", borderBottom: "1px solid", borderColor: "#ffffff1f" }}>
      <Typography id={`${popupId}-title`} variant="subtitle2" sx={{ flex: 1, color: "inherit", fontWeight: 700 }}>{t("stock.dialogTitle")}</Typography>
      <IconButton size="small" aria-label={t("common.close")} onClick={() => { setOpen(false); resetWide(); }} sx={{ color: "inherit", "&:hover": { bgcolor: "#ffffff1f" } }}><CloseIcon fontSize="small" /></IconButton>
    </Box>
    <ProductStockSearchResult result={stock} wide={wideLayout} />
    <Box sx={{ px: 1, py: 0.75, borderTop: "1px solid", borderColor: "divider" }}>
      <Button type="button" fullWidth size="small" onClick={submit}>{t("stockSearch.requests")}</Button>
    </Box>
  </Paper>;
  return <ClickAwayListener onClickAway={() => dismiss()}><Box component="form" ref={anchor} className={`adl-search${inline ? " adl-search-inline" : ""}`} role="search" aria-label={t("adaltra.search")} onSubmit={submit}
    onKeyDown={event => { if (event.key === "Escape" && (inline || showStock)) { event.preventDefault(); dismiss(true); event.stopPropagation(); } }}>
    <TextField type="search" size="small" autoFocus={inline} value={query} onChange={event => { setQuery(event.target.value); setOpen(true); if (event.target.value.trim().length < 2) resetWide(); }} onFocus={() => setOpen(true)}
      placeholder={t(staff ? "stockSearch.hint" : "adaltra.searchHint")}
      slotProps={{ htmlInput: { "aria-label": t(staff ? "stockSearch.label" : "adaltra.search"), maxLength: 120,
        "aria-expanded": staff ? showStock : undefined, "aria-controls": showStock ? popupId : undefined }, input: {
        startAdornment: <InputAdornment position="start"><IconButton type="submit" aria-label={t("adaltra.searchSubmit")} size="small"><SearchIcon sx={{ fontSize: 17 }} /></IconButton></InputAdornment>,
        ...(inline ? { endAdornment: <InputAdornment position="end"><IconButton type="button" size="small" aria-label={t("common.close")} onClick={() => dismiss(true)}><CloseIcon sx={{ fontSize: 18 }} /></IconButton></InputAdornment> } : {}),
      } }} />
    {inline ? result : showStock && <Popper open anchorEl={() => anchor.current} placement="bottom-start"
      onTransitionEnd={event => { if (event.target === event.currentTarget && event.propertyName === "width" && wideWidth) setWide(current => ({ ...current, layout: true })); }}
      sx={{ zIndex: theme => theme.zIndex.modal + 1, width: wideWidth ? `${wideWidth}px` : "min(460px, calc(100vw - 32px))",
        transition: "width 320ms cubic-bezier(0.2, 0.8, 0.2, 1)", "@media (prefers-reduced-motion: reduce)": { transition: "none" } }}
      modifiers={[{ name: "offset", options: { offset: [0, 10] } }, { name: "flip", enabled: false }, { name: "preventOverflow", options: { padding: 16 } }]}>
      {result}
    </Popper>}
  </Box></ClickAwayListener>;
}
